import { useRef, useState } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { orariAperturaApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Upload, Trash2, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'

type BatchStatus = 'success' | 'warning' | 'error'

interface Batch {
  id: number
  filename: string
  import_date: string
  total_records: number
  frazionari_matched: number
  frazionari_not_found: number
  status: BatchStatus
  unmatched_list: string[]
}

const STATUS_ICON = {
  success: <CheckCircle size={15} className="text-emerald-500" />,
  warning: <AlertTriangle size={15} className="text-amber-500" />,
  error: <XCircle size={15} className="text-red-500" />,
}

const STATUS_LABEL: Record<BatchStatus, string> = {
  success: 'Successo',
  warning: 'Avviso',
  error: 'Errore',
}

export default function OrariApertura() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const canImport = ['dispatcher', 'admin', 'superadmin'].includes(user?.role ?? '')

  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [importResult, setImportResult] = useState<any>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const { data: history, isLoading } = useQuery<Batch[]>({
    queryKey: ['orari-history'],
    queryFn: () => orariAperturaApi.history().then(r => r.data),
    enabled: canImport,
  })

  const [aggiornaResult, setAggiornaResult] = useState<any>(null)

  const aggiornaMutation = useMutation({
    mutationFn: () => orariAperturaApi.aggiornaPratiche(),
    onSuccess: (res) => {
      setAggiornaResult(res.data)
      qc.invalidateQueries({ queryKey: ['orari-apertura'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success(`Pratiche aggiornate: ${res.data.aggiornate}`)
    },
    onError: (err: any) => {
      toast.error('Aggiornamento fallito: ' + (err.response?.data?.detail || 'errore sconosciuto'))
    },
  })

  const importMutation = useMutation({
    mutationFn: (file: File) => orariAperturaApi.import(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['orari-history'] })
      qc.invalidateQueries({ queryKey: ['orari-apertura'] })
      setImportResult(res.data)
      if (res.data.status !== 'error') {
        aggiornaMutation.mutate()
      }
    },
    onError: (err: any) => {
      toast.error('Import fallito: ' + (err.response?.data?.detail || 'errore sconosciuto'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => orariAperturaApi.deleteBatch(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orari-history'] })
      toast.success('Import eliminato')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.match(/\.xls[x]?$/i)) { toast.error('Seleziona un file .xls o .xlsx'); e.target.value = ''; return }
    setImportResult(null)
    importMutation.mutate(f)
    e.target.value = ''
  }

  const toggleExpand = (id: number) =>
    setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <div className="space-y-6 max-w-4xl">
      {ConfirmModal}
      <div>
        <h1 className="page-title">Orari Apertura Poste</h1>
        <p className="page-subtitle">Importa e gestisci gli orari di apertura degli uffici per periodo</p>
      </div>

      {/* Upload card */}
      {canImport && (
        <div className="card p-6">
          <h2 className="text-base font-semibold mb-1">Importa file orari</h2>
          <p className="text-sm text-slate-500 mb-4">
            Formato atteso: colonna A = Frazionario, colonne H+ = coppie data/orario (apertura + chiusura)
          </p>

          <div
            className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors"
            style={{ borderColor: importMutation.isPending ? 'var(--signal)' : '#cbd5e1' }}
            onClick={() => !importMutation.isPending && fileRef.current?.click()}
          >
            <Upload size={28} className="mx-auto mb-3" style={{ color: 'var(--signal)' }} />
            {importMutation.isPending ? (
              <p className="text-sm font-medium" style={{ color: 'var(--signal)' }}>Importazione in corso...</p>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-700">Trascina qui il file o clicca per selezionare</p>
                <p className="text-xs text-slate-400 mt-1">Accetta .xls e .xlsx</p>
              </>
            )}
            <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleFile} />
          </div>

          {/* Risultato import */}
          {importResult && (
            <div className={`mt-4 rounded-xl p-4 text-sm ${
              importResult.status === 'success' ? 'bg-emerald-50 border border-emerald-200' :
              importResult.status === 'warning' ? 'bg-amber-50 border border-amber-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-2 font-semibold mb-1">
                {STATUS_ICON[importResult.status as BatchStatus]}
                {importResult.status === 'success' && `✓ Importati ${importResult.total_records} record per ${importResult.matched} siti`}
                {importResult.status === 'warning' && `✓ Importati ${importResult.total_records} record. ${importResult.unmatched} siti non trovati nelle pratiche.`}
                {importResult.status === 'error' && 'Import fallito'}
              </div>
              {importResult.warning && (
                <p className="text-xs text-amber-700 mt-1">{importResult.warning}</p>
              )}
            </div>
          )}

          {/* Risultato aggiornamento pratiche */}
          {aggiornaResult && (
            <div className="mt-3 rounded-xl p-4 text-sm bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-2 font-semibold text-blue-800 mb-1">
                <RefreshCw size={14} />
                Pratiche aggiornate con orari di apertura
              </div>
              <p className="text-xs text-blue-700">
                {aggiornaResult.aggiornate} aggiornate · {aggiornaResult.senza_orario} senza orario · {aggiornaResult.chiuse} uffici chiusi
              </p>
            </div>
          )}

          {/* Pulsante aggiorna pratiche manuale */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Sincronizza manualmente <code className="font-mono bg-slate-100 px-1 rounded">ore_appuntamento</code> delle pratiche attive con gli orari importati
            </p>
            <button
              onClick={() => { setAggiornaResult(null); aggiornaMutation.mutate() }}
              disabled={aggiornaMutation.isPending}
              className="btn-secondary flex items-center gap-2 text-sm whitespace-nowrap ml-4"
            >
              <RefreshCw size={14} className={aggiornaMutation.isPending ? 'animate-spin' : ''} />
              {aggiornaMutation.isPending ? 'Aggiornamento...' : 'Aggiorna pratiche'}
            </button>
          </div>
        </div>
      )}

      {/* Storico import */}
      {canImport && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b">
            <h2 className="text-base font-semibold">Storico import</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.02)', borderBottom: '1px solid var(--border)' }}>
                  {['Data', 'File', 'Record', 'Matched', 'Non trovati', 'Stato', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td></tr>
                ) : !history?.length ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessun import effettuato</td></tr>
                ) : history.map(b => (
                  <>
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                        {b.import_date ? format(new Date(b.import_date), 'dd/MM/yy HH:mm', { locale: it }) : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800 max-w-[180px] truncate" title={b.filename}>{b.filename}</td>
                      <td className="px-4 py-3 text-slate-600">{b.total_records.toLocaleString()}</td>
                      <td className="px-4 py-3 text-emerald-700 font-semibold">{b.frazionari_matched}</td>
                      <td className="px-4 py-3">
                        {b.frazionari_not_found > 0 ? (
                          <span className="text-amber-600 font-semibold">{b.frazionari_not_found}</span>
                        ) : <span className="text-slate-400">0</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${
                          b.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                          b.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {STATUS_ICON[b.status]}
                          {STATUS_LABEL[b.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {b.unmatched_list?.length > 0 && (
                            <button onClick={() => toggleExpand(b.id)}
                              className="btn-ghost px-2 py-1 text-xs text-slate-500">
                              {expanded.has(b.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => confirmDelete('Eliminare questo import e tutti i relativi orari?', () => deleteMutation.mutate(b.id))}
                              className="btn-ghost px-2 py-1 text-red-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded.has(b.id) && b.unmatched_list?.length > 0 && (
                      <tr key={`exp-${b.id}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td colSpan={7} className="px-6 py-3 bg-amber-50">
                          <p className="text-xs font-semibold text-amber-700 mb-1">Frazionari non trovati nelle pratiche:</p>
                          <p className="text-xs text-amber-600 font-mono">{b.unmatched_list.join(', ')}</p>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
